/**
 *
 * Copyright 2020-2026 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

import { HrTime } from '@opentelemetry/api'
import { addHrTimes, hrTimeToMilliseconds, millisToHrTime } from '@opentelemetry/core'
import { InstrumentationBase, InstrumentationConfig } from '@opentelemetry/instrumentation'
import { ReadableSpan } from '@opentelemetry/sdk-trace-base'
import { Metric, onCLS, onINP, onLCP, ReportOpts } from 'web-vitals'

import { SessionManager } from '../managers'
import { SplunkOtelWebConfig } from '../types'
import { VERSION } from '../version'

const MODULE_NAME = 'splunk-webvitals'

export interface SplunkWebVitalsInstrumentationConfig extends InstrumentationConfig {
	/**
	 * If true, the webvitals spans will have their start time aligned with the document load span,
	 * and will inherit the URL attributes from the document load span if available.
	 * @default true
	 */
	alignWebVitalsSpansWithDocumentLoad?: boolean
	cls?: boolean | ReportOpts
	inp?: boolean | ReportOpts
	lcp?: boolean | ReportOpts
}

export class SplunkWebVitalsInstrumentation extends InstrumentationBase<SplunkWebVitalsInstrumentationConfig> {
	private areCallbackAttached = false

	private docLoadSpanPromise: Promise<ReadableSpan>

	private docLoadSpanResolve: ((span: ReadableSpan) => void) | undefined

	private isRecording = false

	private reported: Record<string, boolean> = {}

	constructor(
		config: SplunkWebVitalsInstrumentationConfig = {},
		protected otelConfig: SplunkOtelWebConfig,
		public sessionManager?: SessionManager,
	) {
		super(MODULE_NAME, VERSION, config)

		this.docLoadSpanPromise = new Promise<ReadableSpan>((resolve) => {
			this.docLoadSpanResolve = resolve
		})

		this.otelConfig.spanEmitter?.addEventListener('document-load', (span) => {
			// `document-load` component emits both documentFetch and documentLoad spans.
			// We only want the documentLoad span for alignment.
			if (span.name !== 'documentLoad') {
				return
			}

			this.docLoadSpanResolve?.(span)
			this.docLoadSpanResolve = undefined
		})
	}

	disable(): void {
		this.isRecording = false
	}

	enable(): void {
		this.isRecording = true
		if (this.areCallbackAttached) {
			return
		}

		this.areCallbackAttached = true

		if (this._config.cls !== false) {
			onCLS(
				(metric) => {
					void this.reportMetric('cls', metric)
				},
				typeof this._config.cls === 'object' ? this._config.cls : undefined,
			)
		}

		if (this._config.lcp !== false) {
			onLCP(
				(metric) => {
					void this.reportMetric('lcp', metric)
				},
				typeof this._config.lcp === 'object' ? this._config.lcp : undefined,
			)
		}

		if (this._config.inp !== false) {
			onINP(
				(metric) => {
					void this.reportMetric('inp', metric)
				},
				typeof this._config.inp === 'object' ? this._config.inp : undefined,
			)
		}
	}

	init(): void {}

	private async reportMetric(name: string, metric: Metric): Promise<void> {
		if (!this.isRecording) {
			return
		}

		if (this.reported[name]) {
			return
		}

		this.reported[name] = true

		const value = metric.value
		if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
			return
		}

		const now = Date.now()
		let endTime: HrTime | number = now
		let span
		if (this.shouldAlignWithDocumentLoad()) {
			const docLoadSpan = await this.docLoadSpanPromise
			const alignedStartTime = addHrTimes(docLoadSpan.startTime, millisToHrTime(1))
			let startTime: HrTime | number = alignedStartTime
			endTime = startTime
			const sessionState = this.sessionManager?.getSessionMetadata()
			if (sessionState && sessionState.sessionStart - hrTimeToMilliseconds(alignedStartTime) > 1000 * 60) {
				// If the document load started more than 1 minute before the current session began,
				// it likely belongs to a previous session. In that case, align the span to the
				// current session's start time. The 1-minute tolerance accounts for the fact that
				// the document load timestamp is based on the performance timeline origin,
				// which can precede the session start.
				startTime = sessionState.sessionStart
				endTime = sessionState.sessionStart
			}

			span = this.tracer.startSpan('webvitals', { startTime })
			const docLoadLocation = docLoadSpan?.attributes['location.href']
			if (docLoadLocation) {
				span.setAttribute('location.href', docLoadLocation)
			}
		} else {
			span = this.tracer.startSpan('webvitals', { startTime: now })
		}

		span.setAttribute(name, value)
		span.end(endTime)
	}

	private shouldAlignWithDocumentLoad(): boolean {
		return this._config.alignWebVitalsSpansWithDocumentLoad ?? true
	}
}
